import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { localeFullCode } from '../../../shared/useUtils';
import { ReviewsCarouselStyle } from '../../../styles/components/common/blocks';

export interface IReviewsCarouselProps {
  averageScore: number;
  totalReviews: number;
  topTipStart: string;
  topTipEnd: string;
  title: string;
  description: string;
  bottomTip: string;
  defaultLinkPath?: string;
  reviews: {
    text: string;
    username: string;
    score: number;
    date: string;
    link?: string;
  }[];
}

const SLIDE_DURATION = 4000; // 4 seconds

const ReviewsCarousel = (props: IReviewsCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const router = useRouter();

  const currentReview = props.reviews[currentIndex];
  const totalReviews = props.reviews.length;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalReviews);
    setProgress(0);
  }, [totalReviews]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalReviews) % totalReviews);
    setProgress(0);
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
  };

  // Touch event handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsPaused(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }

    setTouchStart(null);
    setTouchEnd(null);
    setIsPaused(false);
  };

  const localeFullCode = (locale: string) => {
    switch (locale) {
      case 'es':
        return 'es-ES';
      case 'en':
        return 'en-US';
      case 'fr':
        return 'fr-FR';
      default:
        return 'en-US';
    }
  };

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext();
          return 0;
        }
        return prev + 100 / (SLIDE_DURATION / 50);
      });
    }, 50);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, goToNext]);

  const renderStars = (score: number) => {
    return (
      <div className={ReviewsCarouselStyle['reviews-carousel-items-stars']}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Image
            key={star}
            src={
              star <= score
                ? '/assets/images/icons/StarSolid.svg'
                : '/assets/images/icons/StarEmpty.svg'
            }
            alt=""
            width={20}
            height={20}
            className="colorize-primary"
          />
        ))}
      </div>
    );
  };

  return (
    <div className={ReviewsCarouselStyle['reviews-carousel']}>
      <div className={ReviewsCarouselStyle['reviews-carousel-text']}>
        <div>
          {/* <div
            className={ReviewsCarouselStyle['reviews-carousel-average-score']}>
            <Image
              src="/assets/images/icons/StarSolid.svg"
              alt="Average Score"
              width={20}
              height={20}
              className="colorize-primary"
            />
            <strong>{props.averageScore}/5</strong>
            <span>·</span>
            <span>{props.topTipStart}</span>
            <strong>
              {props.totalReviews} {props.topTipEnd}
            </strong>
          </div> */}
          <h2>{props.title}</h2>
          <p>{props.description}</p>
        </div>
        {/* <span className={ReviewsCarouselStyle['reviews-carousel-bottom-tip']}>
          {props.bottomTip}
        </span> */}
      </div>

      {/* <div
        className={ReviewsCarouselStyle['reviews-carousel-items']}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}>
        <div
          className={
            ReviewsCarouselStyle['reviews-carousel-items-progress-bars']
          }>
          {props.reviews.map((_, index) => (
            <div
              key={index}
              className={
                ReviewsCarouselStyle[
                  'reviews-carousel-items-progress-bar-container'
                ]
              }
              onClick={() => goToIndex(index)}>
              <div
                className={
                  ReviewsCarouselStyle['reviews-carousel-items-progress-bar']
                }
                style={{
                  width:
                    index === currentIndex
                      ? `${progress}%`
                      : index < currentIndex
                      ? '100%'
                      : '0%',
                }}
              />
            </div>
          ))}
        </div>

        <div className={ReviewsCarouselStyle['reviews-carousel-items-logo']}>
          <Image
            src="/assets/images/common/trustpilot-logo-dark.svg"
            alt="Trustpilot"
            width={98}
            height={24}
          />
        </div>

        <div className={ReviewsCarouselStyle['reviews-carousel-items-content']}>
          {renderStars(currentReview.score)}
          <p className={ReviewsCarouselStyle['reviews-carousel-items-text']}>
            {currentReview.text}
          </p>
          <div
            className={ReviewsCarouselStyle['reviews-carousel-items-footer']}>
            <div
              className={ReviewsCarouselStyle['reviews-carousel-items-author']}>
              <span
                className={
                  ReviewsCarouselStyle['reviews-carousel-items-username']
                }>
                {currentReview.username}
              </span>
              <span
                className={ReviewsCarouselStyle['reviews-carousel-items-date']}>
                {currentReview.date}
              </span>
            </div>
            {currentReview.link && (
              <a
                href={currentReview.link}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  ReviewsCarouselStyle['reviews-carousel-items-external-link']
                }
                aria-label="View full review">
                <Image
                  src="/assets/images/icons/ExternalLink.svg"
                  alt={currentReview.link}
                  width={20}
                  height={20}
                />
              </a>
            )}
            {!currentReview.link && props.defaultLinkPath && (
              <a
                href={props.defaultLinkPath}
                className={
                  ReviewsCarouselStyle['reviews-carousel-items-external-link']
                }
                aria-label="View full review">
                <Image
                  src="/assets/images/icons/ExternalLink.svg"
                  alt={props.defaultLinkPath}
                  width={20}
                  height={20}
                />
              </a>
            )}
            <div
              className={
                ReviewsCarouselStyle['reviews-carousel-items-quote-icon']
              }>
              <Image
                src="/assets/images/icons/Quote.svg"
                alt=""
                className="colorize-primary"
                width={90}
                height={90}
              />
            </div>
          </div>
        </div>

        <button
          className={`${ReviewsCarouselStyle['reviews-carousel-items-nav-arrow']} ${ReviewsCarouselStyle['reviews-carousel-items-nav-arrow-prev']}`}
          onClick={goToPrev}
          aria-label="Previous review">
          <Image
            src="/assets/images/icons/NavArrowLeft.svg"
            alt=""
            width={24}
            height={24}
          />
        </button>
        <button
          className={`${ReviewsCarouselStyle['reviews-carousel-items-nav-arrow']} ${ReviewsCarouselStyle['reviews-carousel-items-nav-arrow-next']}`}
          onClick={goToNext}
          aria-label="Next review">
          <Image
            src="/assets/images/icons/NavArrowRight.svg"
            alt=""
            width={24}
            height={24}
          />
        </button>
      </div> */}

      <div>
        {/* TrustBox widget - Mini */}
        <div
          className="trustpilot-widget"
          data-locale={localeFullCode(router.locale || 'en')}
          data-template-id="53aa8807dec7e10d38f59f32"
          data-businessunit-id="57583e6f0000ff00058f82f6"
          data-style-height="120px"
          data-style-width="100%"
          data-token="d50c17a5-689e-40aa-8a6d-902c22a890d7">
          <a
            href={`https://${
              router.locale !== 'en' ? router.locale : 'www'
            }.trustpilot.com/review/igerent.com`}
            target="_blank"
            rel="noopener">
            Trustpilot
          </a>
        </div>
        {/* End TrustBox widget */}
      </div>
    </div>
  );
};

export default ReviewsCarousel;
